import React, { useMemo, useState, useEffect } from 'react';
import {
    Box,
    Button,
    Typography,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    CircularProgress,
    Paper,
} from '@mui/material';
import { ArrowForward as BackIcon } from '@mui/icons-material';
import useFetch from '../../../../../hooks/useFetch';
import { API } from '../../../../../utils/apiRoutes';
import { toPersianDigits } from '../../../../../utils/convertNumbers';
import PersianDatePicker from '../../../../common/PersianDatePicker';
import TablePagination from '../../UserList/TablePagination';

const formatCurrency = (amount) => toPersianDigits(new Intl.NumberFormat('fa-IR').format(amount || 0));

const formatDate = (d) => {
    if (!d) return '-';
    try {
        return toPersianDigits(new Date(d).toLocaleDateString('fa-IR'));
    } catch {
        return d;
    }
};

// Credit-normal: balance > 0 means the student has prepaid (بستانکار); < 0 means
// the student owes the institute (بدهکار).
const balanceLabel = (v) => {
    const n = v || 0;
    if (n === 0) return formatCurrency(0);
    return `${formatCurrency(Math.abs(n))} ${n > 0 ? 'بستانکار' : 'بدهکار'}`;
};
const balanceColor = (v) => ((v || 0) < 0 ? 'error.main' : 'success.main');

function StudentLedger({ enrollment, onBack }) {
    const [dateRange, setDateRange] = useState({ start_date: '', end_date: '' });

    const params = useMemo(() => {
        if (!enrollment?.enrollment_id) return undefined;
        const p = { enrollment_id: enrollment.enrollment_id };
        if (dateRange.start_date) p.start_date = dateRange.start_date;
        if (dateRange.end_date) p.end_date = dateRange.end_date;
        return p;
    }, [enrollment?.enrollment_id, dateRange]);

    const { data, loading } = useFetch(API.receivablesLedger, {
        params,
        skip: !enrollment?.enrollment_id,
    });

    const rows = useMemo(() => data?.rows || [], [data]);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);
    const pagedRows = useMemo(
        () => rows.slice(page * rowsPerPage, (page + 1) * rowsPerPage),
        [rows, page, rowsPerPage]
    );
    // Reset to first page when the enrollment or date range changes.
    useEffect(() => { setPage(0); }, [enrollment?.enrollment_id, dateRange]);

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', mb: 3 }}>
                <Button startIcon={<BackIcon />} onClick={onBack} variant="outlined">
                    بازگشت به مطالبات
                </Button>
                <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                    صورت‌حساب {enrollment?.student_name}
                    {enrollment?.course_name ? ` — ${enrollment.course_name}` : ''}
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', ml: 'auto' }}>
                    <Box sx={{ width: 160 }}>
                        <PersianDatePicker
                            label="از تاریخ"
                            value={dateRange.start_date ? new Date(dateRange.start_date) : null}
                            onChange={(d) => setDateRange((prev) => ({
                                ...prev,
                                start_date: (d && !isNaN(d.getTime())) ? d.toISOString().split('T')[0] : '',
                            }))}
                        />
                    </Box>
                    <Box sx={{ width: 160 }}>
                        <PersianDatePicker
                            label="تا تاریخ"
                            value={dateRange.end_date ? new Date(dateRange.end_date) : null}
                            onChange={(d) => setDateRange((prev) => ({
                                ...prev,
                                end_date: (d && !isNaN(d.getTime())) ? d.toISOString().split('T')[0] : '',
                            }))}
                        />
                    </Box>
                    {data && (
                        <Typography variant="body2" color="text.secondary">
                            جلسات:{' '}
                            <Box component="span" sx={{ fontWeight: 'bold' }}>
                                {toPersianDigits(data.closing_sessions)}
                            </Box>
                        </Typography>
                    )}
                    {data && (
                        <Typography variant="body2" color="text.secondary">
                            مانده:{' '}
                            <Box component="span" sx={{ fontWeight: 'bold', color: balanceColor(data.closing_balance) }}>
                                {balanceLabel(data.closing_balance)}
                            </Box>
                        </Typography>
                    )}
                </Box>
            </Box>

            {loading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
                    <CircularProgress />
                </Box>
            )}

            {!loading && data && (
                <Paper variant="outlined">
                    <Table size="small" stickyHeader>
                        <TableHead>
                            <TableRow>
                                <TableCell>تاریخ</TableCell>
                                <TableCell>شرح</TableCell>
                                <TableCell align="center">جلسات</TableCell>
                                <TableCell align="center">بدهکار</TableCell>
                                <TableCell align="center">بستانکار</TableCell>
                                <TableCell align="center">باقیمانده</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 'medium' }}>مانده ابتدای دوره</TableCell>
                                <TableCell colSpan={2} />
                                <TableCell align="center">—</TableCell>
                                <TableCell align="center">—</TableCell>
                                <TableCell align="center" sx={{ fontWeight: 'bold', color: balanceColor(data.opening_balance) }}>
                                    {balanceLabel(data.opening_balance)}
                                </TableCell>
                            </TableRow>
                            {rows.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={6}>
                                        <Typography align="center" variant="body2" color="text.secondary">تراکنشی در این بازه ثبت نشده است.</Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                            {pagedRows.map((r, i) => (
                                <TableRow key={page * rowsPerPage + i}>
                                    <TableCell>{formatDate(r.date)}</TableCell>
                                    <TableCell>{r.description}</TableCell>
                                    <TableCell align="center">{toPersianDigits(r.sessions_delta)}</TableCell>
                                    <TableCell align="center" sx={{ color: r.debit ? 'error.main' : 'text.disabled' }}>
                                        {r.debit ? formatCurrency(r.debit) : '—'}
                                    </TableCell>
                                    <TableCell align="center" sx={{ color: r.credit ? 'success.main' : 'text.disabled' }}>
                                        {r.credit ? formatCurrency(r.credit) : '—'}
                                    </TableCell>
                                    <TableCell align="center" sx={{ fontWeight: 'bold', color: balanceColor(r.balance) }}>
                                        {balanceLabel(r.balance)}
                                    </TableCell>
                                </TableRow>
                            ))}
                            <TableRow>
                                <TableCell colSpan={5} sx={{ fontWeight: 'bold' }}>مانده پایان</TableCell>
                                <TableCell align="center" sx={{ fontWeight: 'bold', color: balanceColor(data.closing_balance) }}>
                                    {balanceLabel(data.closing_balance)}
                                </TableCell>
                            </TableRow>
                        </TableBody>
                    </Table>
                    {rows.length > rowsPerPage && (
                        <TablePagination
                            totalCount={rows.length}
                            page={page}
                            rowsPerPage={rowsPerPage}
                            onPageChange={(newPage) => setPage(newPage)}
                            onRowsPerPageChange={(e) => {
                                setRowsPerPage(parseInt(e.target.value, 10));
                                setPage(0);
                            }}
                            responsive
                        />
                    )}
                </Paper>
            )}
        </Box>
    );
}

export default StudentLedger;
